import { cva, type VariantProps } from "class-variance-authority";
import type { HTMLAttributes } from "react";
import { cn } from "./cn";

export const badgeVariants = cva(
  "inline-flex shrink-0 items-center justify-center gap-1 font-medium whitespace-nowrap rounded-sm border transition-colors outline-none",
  {
    variants: {
      variant: {
        default:
          "border-transparent bg-[var(--accent)] text-[var(--accent-foreground)] font-semibold shadow-xs",
        secondary: "border-transparent bg-[var(--hover-strong)] text-[var(--text)]",
        outline: "border-[var(--border)] bg-transparent text-[var(--text)]",
        accent:
          "border-[var(--accent-border)] bg-[var(--accent-subtle)] text-[var(--accent-text)] font-semibold",
        success:
          "border-[var(--success-border)] bg-[var(--success-subtle)] text-[var(--success-text)]",
        warning:
          "border-[var(--warning-border)] bg-[var(--warning-subtle)] text-[var(--warning-text)]",
        destructive:
          "border-[var(--destructive-border)] bg-[var(--destructive-subtle)] text-[var(--destructive-text)]",
        subtle: "border-transparent bg-[var(--hover)] text-[var(--muted)]",
      },
      size: {
        default: "h-5 px-2 text-[11px]",
        sm: "h-4 px-1.5 text-[10px] rounded-[3px]",
        lg: "h-6 px-2.5 text-xs",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface BadgeProps
  extends HTMLAttributes<HTMLSpanElement>, VariantProps<typeof badgeVariants> {}

export function Badge({ className, variant, size, ...props }: BadgeProps) {
  return (
    <span
      className={cn(badgeVariants({ variant, size }), className)}
      data-slot="badge"
      {...props}
    />
  );
}
